/**
 * Mobile-internal league context hook for Free Agents page.
 * Per Tony's mobile architecture rule: mobile-internal copy of desktop useLeagueContext.
 *
 * Side effects:
 *   - Mount: checkLeagueStatus() FIRST (prevents "Transactions Not Available" flash),
 *     then parallel leagueSettings + savedPrices load
 *   - 5-sec poll: while leagueStatus is 'setup' or 'pricing' AND !browseMode
 *   - Cascading price lookup: checkPriceStatus → getSalarySettings → getPlayerPrices
 *   - Listens to leagueStatus transitions to start/stop the poll interval
 *
 * Shared dependencies (allowed per architecture rule):
 *   - services/apiService (leaguesAPI methods only)
 */

import { useState, useEffect, useCallback } from 'react';
import { leaguesAPI } from '../../../../../services/apiService';

export const useMobileLeagueContext = (leagueId, isCommissionerMode = false) => {
  // League Configuration State
  const [leagueSettings, setLeagueSettings] = useState({});
  const [leagueStatus, setLeagueStatus] = useState(null);
  const [savedPrices, setSavedPrices] = useState({});

  // Transaction State
  const [transactionsEnabled, setTransactionsEnabled] = useState(false);
  const [pricesExist, setPricesExist] = useState(false);
  const [noPricesWarning, setNoPricesWarning] = useState(false);
  const [browseMode, setBrowseMode] = useState(false);

  // Initialization State
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const loadLeagueSettings = useCallback(async () => {
    try {
      const response = await leaguesAPI.getLeagueSettings(leagueId);
      if (response.success) {
        setLeagueSettings(response.settings);
      }
    } catch (err) {
      console.error('Error loading league settings:', err);
    } finally {
      setSettingsLoaded(true);
    }
  }, [leagueId]);

  const loadSavedPrices = useCallback(async () => {
    try {
      let response;

      // 1) checkPriceStatus
      if (typeof leaguesAPI.checkPriceStatus === 'function') {
        const priceStatus = await leaguesAPI.checkPriceStatus(leagueId);
        if (priceStatus.success) {
          if (priceStatus.has_prices) {
            setNoPricesWarning(false);
            setPricesExist(true);
          } else {
            setPricesExist(false);
          }
          if (priceStatus.prices) {
            response = { success: true, prices: priceStatus.prices };
          }
        }
      }

      // 2) getSalarySettings
      if (!response && typeof leaguesAPI.getSalarySettings === 'function') {
        const salaryResponse = await leaguesAPI.getSalarySettings(leagueId);
        if (salaryResponse.success && salaryResponse.existing_prices) {
          response = { success: true, prices: salaryResponse.existing_prices };
          setPricesExist(true);
        }
      }

      // 3) getPlayerPrices
      if (!response && typeof leaguesAPI.getPlayerPrices === 'function') {
        response = await leaguesAPI.getPlayerPrices(leagueId);
        if (response && response.success && response.prices) {
          setPricesExist(true);
        }
      }

      // Convert prices array to map keyed by mlb id
      if (response && response.success && response.prices) {
        const priceMap = {};
        response.prices.forEach((p) => {
          const playerId = p.ids?.mlb || p.player_id;
          priceMap[playerId] = p.financial?.baseline_price || 1.0;
        });
        setSavedPrices(priceMap);
      } else {
        setSavedPrices({});
        setPricesExist(false);
      }
    } catch (err) {
      console.error('Error loading saved prices:', err);
      setSavedPrices({});
      setPricesExist(false);
    }
  }, [leagueId]);

  const checkLeagueStatus = useCallback(async () => {
    try {
      const response = await leaguesAPI.getLeagueDetails(leagueId);
      if (response.success) {
        const status = response.league.league_status || response.league.status || 'setup';
        const draftType =
          response.league.draft_type ||
          response.league.draft_settings?.type ||
          response.league.season_setup?.draft_type ||
          null;
        const isOfflineDraft = draftType === 'offline';

        if (isOfflineDraft) {
          setLeagueStatus('offline_draft');
          setTransactionsEnabled(true);
          setNoPricesWarning(false);
          setBrowseMode(false);
        } else {
          setLeagueStatus(status);
          if (status === 'setup') {
            setTransactionsEnabled(false);
            setNoPricesWarning(true);
            setBrowseMode(false);
          } else {
            setTransactionsEnabled(true);
            setNoPricesWarning(false);
            setBrowseMode(false);
          }
        }
      }
    } catch (err) {
      console.error('Error checking league status:', err);
      // Defensive: assume active on error
      setTransactionsEnabled(true);
      setNoPricesWarning(false);
    }
  }, [leagueId]);

  const handleBrowseAnyway = useCallback(() => {
    setBrowseMode(true);
    setNoPricesWarning(false);
  }, []);

  // Mount: status FIRST, then parallel settings + prices
  useEffect(() => {
    if (leagueId && !initialized) {
      setInitialized(true);

      const initializeContext = async () => {
        if (isCommissionerMode) {
          setTransactionsEnabled(true);
          setNoPricesWarning(false);
          setBrowseMode(false);
        }
        await checkLeagueStatus();
        await Promise.all([loadLeagueSettings(), loadSavedPrices()]);
      };

      initializeContext();
    }
  }, [leagueId, initialized, isCommissionerMode, loadLeagueSettings, loadSavedPrices, checkLeagueStatus]);

  // 5-sec poll while leagueStatus is setup/pricing AND !browseMode.
  // Memory-leak guard: cleanup clears the interval on unmount OR status transition.
  useEffect(() => {
    if ((leagueStatus === 'setup' || leagueStatus === 'pricing') && !browseMode) {
      const interval = setInterval(() => {
        checkLeagueStatus();
        loadSavedPrices();
      }, 5000);

      return () => clearInterval(interval);
    }
    return undefined;
  }, [leagueStatus, browseMode, checkLeagueStatus, loadSavedPrices]);

  return {
    // State values
    leagueSettings,
    leagueStatus,
    savedPrices,
    transactionsEnabled,
    pricesExist,
    noPricesWarning,
    browseMode,
    settingsLoaded,

    // Functions
    loadLeagueSettings,
    loadSavedPrices,
    checkLeagueStatus,
    handleBrowseAnyway,

    // Computed values
    isReady: settingsLoaded && initialized,
    canTransact: transactionsEnabled || browseMode,
  };
};

export default useMobileLeagueContext;
